import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { RiskStatus } from '@app/src/types/resourceExplorer'
import type { SustainabilityTopicProviderRisk } from '@app/src/types/topicalRisk'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import { useMemo } from 'react'

interface UseProviderOverallRisksResult {
  overallRiskByProviderId: Map<number, RiskStatus>
  isLoading: boolean
}

// useFetchCollectionWithPost hands back a fresh [] on every render until the query resolves, so
// deriving a map from it unconditionally would hand out a new Map identity forever while the request
// is pending, disabled or failed - and every consumer rebuilding off that identity would spin.
const NO_RISKS: Map<number, RiskStatus> = new Map()

const toOverallRiskByProviderId = (providerRisks: SustainabilityTopicProviderRisk[]): Map<number, RiskStatus> =>
  new Map(providerRisks.map(providerRisk => [providerRisk.providerId, providerRisk.riskStatus]))

export const useProviderOverallRisks = (providerIds: number[]): UseProviderOverallRisksResult => {
  // Sorted so the query key is the same page whichever order the rows arrive in - re-sorting the table
  // then reuses the cached result instead of refetching.
  const sortedProviderIds = [...providerIds].sort((a, b) => a - b)

  const { items = [], isLoading } = useFetchCollectionWithPost<SustainabilityTopicProviderRisk>({
    key: [FetchKey.ProviderRisk, 'provider-collection-overall-risk'],
    endpoint: endpoints.topicalRisk,
    payload: {
      filter: [
        { name: 'providerId', filters: [{ operator: Operators.In, value: sortedProviderIds }] },
        { name: 'isLatest', filters: [{ operator: Operators.EqualTo, value: true }] },
      ],
      include: [],
      pagination: { pageNumber: 1, itemsPerPage: sortedProviderIds.length || 1 },
    },
    options: { staleTime: ONE_MINUTE_MS, enabled: Boolean(sortedProviderIds.length) },
  })

  const overallRiskByProviderId = useMemo(() => (items.length ? toOverallRiskByProviderId(items) : NO_RISKS), [items])

  return { overallRiskByProviderId, isLoading }
}
